/**
 * NssTrendSparkline — 3-year HESA NSS Q26 satisfaction trend for a UK
 * uni. Tiny inline SVG so it fits under the Satisfaction score
 * without needing recharts.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TrendingDown, TrendingUp, Minus } from "lucide-react";

export default function NssTrendSparkline({ uniId }) {
  const [pts, setPts] = useState(null);
  useEffect(() => {
    if (!uniId) return;
    api.get(`/methodology/nss-2025/trend?uni_id=${uniId}`)
       .then((r) => setPts(r.data.points || []))
       .catch(() => setPts([]));
  }, [uniId]);

  if (!pts || pts.length < 2) return null;

  const values = pts.map((p) => p.positivity_pct);
  const min = Math.min(...values) - 2;
  const max = Math.max(...values) + 2;
  const range = max - min || 1;
  const W = 100;
  const H = 24;
  const points = pts.map((p, i) => {
    const x = (i / (pts.length - 1)) * W;
    const y = H - ((p.positivity_pct - min) / range) * H;
    return [x, y, p];
  });
  const path = points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const first = values[0];
  const last = values[values.length - 1];
  const delta = Math.round((last - first) * 10) / 10;
  const Icon = delta > 0.5 ? TrendingUp : delta < -0.5 ? TrendingDown : Minus;
  const color = delta > 0.5 ? "text-emerald-700" : delta < -0.5 ? "text-red-700" : "text-stone-500";

  return (
    <div
      className="mt-1.5 inline-flex items-center gap-2 text-[11px] font-mono"
      data-testid="nss-trend-sparkline"
      title={pts.map((p) => `NSS ${p.cycle}: ${p.positivity_pct}%`).join(" · ")}
    >
      <svg width={W} height={H} className="shrink-0">
        <path d={path} fill="none" stroke="#4C1D95" strokeWidth={1.5} />
        {points.map(([x, y, p], i) => (
          <circle key={i} cx={x} cy={y} r={2}
                  fill={i === points.length - 1 ? "#4C1D95" : "#a8a29e"} />
        ))}
      </svg>
      <span className={`inline-flex items-center gap-1 ${color}`}>
        <Icon className="h-3 w-3" />
        {delta >= 0 ? "+" : ""}{delta} pts (NSS 23→25)
      </span>
    </div>
  );
}
